import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

export default function AIChatbot() {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setIsServiceModalOpen(true);
  };

  const faqs = [
    {
      question: 'How does an AI chatbot differ from a traditional chatbot?',
      answer: 'AI chatbots use advanced natural language processing and machine learning to understand context, learn from conversations, and provide human-like responses. Unlike traditional rule-based chatbots that follow scripted paths, AI chatbots can handle complex queries, understand intent, and adapt their responses based on customer needs.'
    },
    {
      question: 'Can the AI chatbot integrate with my existing CRM and tools?',
      answer: 'Yes, our AI chatbot seamlessly integrates with popular CRM systems like Salesforce, HubSpot, and Zoho, as well as communication platforms like Slack and Microsoft Teams. It can automatically log conversations, update customer records, and trigger workflows in your existing systems.'
    },
    {
      question: 'What languages does the AI chatbot support?',
      answer: 'Our AI chatbot supports over 50 languages and can automatically detect and respond in the customer\'s preferred language. This includes major languages like English, Spanish, French, German, Chinese, Japanese, and many more, enabling you to serve a global customer base.'
    },
    {
      question: 'How long does it take to set up an AI chatbot?',
      answer: 'Setup typically takes 24-48 hours. We handle the technical configuration, train the AI on your business information, and customize responses to match your brand voice. You can start capturing leads and answering customer questions within two business days.'
    },
    {
      question: 'Can the chatbot handle complex customer inquiries?',
      answer: 'Yes, our AI chatbot is designed to handle both simple FAQs and complex multi-turn conversations. It can understand context, ask clarifying questions, and provide detailed answers. For highly specialized queries, it can seamlessly transfer to a human agent with full conversation history.'
    },
    {
      question: 'How much does an AI chatbot cost for small businesses?',
      answer: 'Our AI chatbot pricing is designed for small businesses, starting at affordable monthly rates with no setup fees. Pricing varies based on conversation volume and features needed. Most small businesses find they save money compared to hiring additional customer service staff while providing 24/7 coverage.'
    },
    {
      question: 'Is my customer data secure with the AI chatbot?',
      answer: 'Absolutely. We use enterprise-grade encryption, comply with GDPR and CCPA regulations, and never share your data with third parties. All conversations are stored securely, and you maintain full ownership of your customer data. We also offer custom data retention policies to meet your compliance needs.'
    },
    {
      question: 'Can I customize the chatbot\'s personality and responses?',
      answer: 'Yes, full customization is available. You can define the chatbot\'s tone (professional, friendly, casual), create custom responses for specific scenarios, and train it on your unique business knowledge. The chatbot can reflect your brand personality while maintaining helpful and accurate communication.'
    }
  ];

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  return (
    <>
      <SEO
        title="AI Chatbot Service | Intelligent Customer Support Automation"
        description="Deploy intelligent AI chatbots for your business. Automate customer support, answer questions instantly, and provide 24/7 assistance with advanced conversational AI."
        canonical="/services/ai-chatbot"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'AI Chatbot Service',
              description: 'Intelligent AI chatbot service for automated customer support and engagement',
              provider: {
                '@type': 'Organization',
                name: 'AI Receptionist Service',
                url: import.meta.env.VITE_SITE_URL || 'https://example.com'
              },
              areaServed: 'Worldwide',
              availableChannel: {
                '@type': 'ServiceChannel',
                serviceUrl: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/ai-chatbot`
              }
            }
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: 'What is an AI chatbot?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'An AI chatbot is an intelligent virtual assistant that uses artificial intelligence to communicate with customers, answer questions, and provide support 24/7.'
                  }
                },
                {
                  '@type': 'Question',
                  name: 'How does AI chatbot improve customer service?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'AI chatbots provide instant responses, handle multiple conversations simultaneously, work 24/7, and can resolve common queries without human intervention.'
                  }
                },
                {
                  '@type': 'Question',
                  name: 'Can AI chatbots integrate with existing systems?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Yes, AI chatbots can integrate with CRM systems, booking platforms, and other business tools to provide seamless customer service.'
                  }
                }
              ]
            }
          }
        ]}
      />
      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-white to-secondary/5"></div>
          <div className="absolute inset-0 opacity-30">
            <img
              src="/readdy-assets/ai-chatbot-hero-001.jpg"
              alt="AI Chatbot Technology"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/60 to-white/90"></div>
          
          <div className="relative max-w-4xl mx-auto text-center">
            <h1 className="font-display text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              AI Chatbot for Small Businesses
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Never miss a lead again. Our intelligent AI chatbot engages visitors 24/7, answers questions instantly, and converts conversations into customers while you focus on running your business.
            </p>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="inline-block bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get Your AI Chatbot Today
            </button>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            
            {/* What is AI Chatbot */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                What is an AI Chatbot for Small Businesses?
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                An AI chatbot for small businesses is an intelligent virtual assistant that uses artificial intelligence and natural language processing to communicate with your website visitors and customers in real-time. Unlike traditional chatbots that follow rigid scripts, AI-powered chatbots understand context, learn from interactions, and provide personalized, human-like responses that feel natural and helpful.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                For small businesses, an AI chatbot acts as your always-available team member, handling customer inquiries, capturing leads, <Link to="/ai-appointment-scheduling" className="text-primary hover:underline font-medium">scheduling appointments</Link>, and providing instant support even when you're closed. It's like having a knowledgeable employee working around the clock without the overhead costs of hiring additional staff.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The technology has evolved dramatically in recent years, making enterprise-level AI chatbot capabilities accessible and affordable for small businesses. Today's AI chatbots can understand complex questions, maintain context across conversations, integrate with your existing tools, and continuously improve their responses based on customer interactions.
              </p>
            </div>

            {/* How It Works */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                How Our AI Chatbot Works
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Our AI chatbot is designed specifically for small businesses, combining powerful technology with simple setup and management. Here's how it works:
              </p>
              
              <div className="space-y-6">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-xl text-gray-900 mb-3">1. Instant Deployment</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We install the chatbot on your website with a simple code snippet. Within 24-48 hours, your AI assistant is trained on your business information, products, services, and frequently asked questions. No technical expertise required on your end.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-xl text-gray-900 mb-3">2. Intelligent Engagement</h3>
                  <p className="text-gray-700 leading-relaxed">
                    When visitors land on your website, the chatbot proactively engages them with personalized greetings. It uses AI to understand their questions, whether they're asking about pricing, services, availability, or specific product details, and provides accurate, helpful responses instantly. Combined with <Link to="/services/ai-lead-follow-up" className="text-primary hover:underline font-medium">automated lead follow-up</Link>, every conversation turns into a nurtured opportunity.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-xl text-gray-900 mb-3">3. Lead Capture & Qualification</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The chatbot naturally collects contact information during conversations, qualifying leads by understanding their needs and intent. It can schedule appointments, send quotes, or route high-priority leads directly to your team with full conversation context.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-xl text-gray-900 mb-3">4. Continuous Learning</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Your AI chatbot learns from every interaction, improving its responses over time. You can review conversations, update answers, and train it on new information. The more it interacts with customers, the smarter and more effective it becomes.
                  </p>
                </div>
              </div>
            </div>

            {/* Key Features */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                Key Features of Our AI Chatbot
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="border border-gray-200 p-6 rounded-lg hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-chat-smile-3-line text-2xl text-primary"></i>
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-3">24/7 Availability</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Your chatbot never sleeps, takes breaks, or calls in sick. It provides instant responses to customer inquiries at any time of day or night, ensuring you never miss a potential lead or sale.
                  </p>
                </div>

                <div className="border border-gray-200 p-6 rounded-lg hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-user-search-line text-2xl text-primary"></i>
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-3">Intelligent Lead Capture</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Automatically collects visitor information through natural conversation, qualifying leads based on their responses and routing high-value prospects to your sales team with complete context.
                  </p>
                </div>

                <div className="border border-gray-200 p-6 rounded-lg hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-question-answer-line text-2xl text-primary"></i>
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-3">FAQ Automation</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Handles repetitive questions about hours, pricing, services, and policies automatically, freeing your team to focus on complex issues and high-value customer interactions.
                  </p>
                </div>

                <div className="border border-gray-200 p-6 rounded-lg hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-global-line text-2xl text-primary"></i>
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-3">Multi-Language Support</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Communicate with customers in over 50 languages, automatically detecting their preferred language and responding accordingly to serve a diverse, global customer base.
                  </p>
                </div>

                <div className="border border-gray-200 p-6 rounded-lg hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-links-line text-2xl text-primary"></i>
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-3">CRM Integration</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Seamlessly connects with your existing CRM, email marketing, and business tools, automatically syncing conversations, contact information, and lead data across your systems.
                  </p>
                </div>

                <div className="border border-gray-200 p-6 rounded-lg hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-settings-3-line text-2xl text-primary"></i>
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-3">Full Customization</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Customize the chatbot's appearance, personality, and responses to match your brand. Train it on your specific business knowledge and adjust its behavior based on your preferences.
                  </p>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                Benefits for Your Small Business
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Implementing an AI chatbot delivers measurable results that directly impact your bottom line:
              </p>

              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Capture More Leads</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Studies show that 67% of website visitors leave without taking action. An AI chatbot proactively engages these visitors, increasing lead capture rates by up to 40% by starting conversations and collecting contact information naturally.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Reduce Response Time</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Customers expect instant answers. With an AI chatbot, response time drops from hours or days to seconds, dramatically improving customer satisfaction and increasing the likelihood of conversion.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Lower Customer Service Costs</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      An AI chatbot can handle 80% of routine customer inquiries automatically, reducing the workload on your team and eliminating the need to hire additional customer service staff as you grow.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Scale Without Limits</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Whether you have 10 or 10,000 website visitors, your AI chatbot handles every conversation simultaneously with the same quality and speed, allowing your business to scale without proportional increases in support costs.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Gain Customer Insights</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Every conversation provides valuable data about customer needs, pain points, and frequently asked questions. Use these insights to improve your products, services, and marketing strategies.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Related Services */}
            <div className="mb-16 bg-gradient-to-br from-gray-50 to-primary/5 p-8 rounded-xl">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-6">
                Enhance Your AI Chatbot with Related Services
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Maximize your customer engagement by combining our AI chatbot with these complementary services:
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <Link
                  to="/ai-phone-answering-service"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-lg transition-all cursor-pointer group"
                >
                  <i className="ri-phone-line text-3xl text-primary mb-3 block"></i>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    Phone Answering Service
                  </h3>
                  <p className="text-sm text-gray-600">
                    Extend AI support to phone calls with intelligent call handling and routing.
                  </p>
                </Link>

                <Link
                  to="/services/ai-receptionist"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-lg transition-all cursor-pointer group"
                >
                  <i className="ri-customer-service-2-line text-3xl text-primary mb-3 block"></i>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    AI Receptionist
                  </h3>
                  <p className="text-sm text-gray-600">
                    Combine chat and phone support with a complete AI receptionist solution.
                  </p>
                </Link>

                <Link
                  to="/ai-appointment-scheduling"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-lg transition-all cursor-pointer group"
                >
                  <i className="ri-calendar-check-line text-3xl text-primary mb-3 block"></i>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    Appointment Scheduling
                  </h3>
                  <p className="text-sm text-gray-600">
                    Let your chatbot book appointments automatically with calendar integration.
                  </p>
                </Link>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <h3 className="font-semibold text-gray-900 pr-4">
                        {faq.question}
                      </h3>
                      <i
                        className={`ri-arrow-down-s-line text-2xl text-gray-400 flex-shrink-0 transition-transform ${
                          openFaqIndex === index ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-4 pt-2">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Closing CTA */}
            <div className="bg-gradient-to-br from-primary to-secondary text-white p-12 rounded-2xl text-center">
              <h2 className="font-display text-3xl font-bold mb-4">
                Ready to Transform Your Customer Engagement?
              </h2>
              <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
                Join hundreds of small businesses using AI chatbots to capture more leads, provide better customer service, and grow faster. Get started today with no setup fees.
              </p>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="inline-block bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Schedule Your Free Consultation
              </button>
            </div>

          </div>
        </section>

        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setIsIndustriesModalOpen(true)}
          onOpenHowItWorks={() => setIsHowItWorksModalOpen(true)}
        />

        <ServiceModal
          isOpen={isServiceModalOpen}
          onClose={() => setIsServiceModalOpen(false)}
          title={selectedService}
        />

        <IndustriesModal
          isOpen={isIndustriesModalOpen}
          onClose={() => setIsIndustriesModalOpen(false)}
        />

        <HowItWorksModal
          isOpen={isHowItWorksModalOpen}
          onClose={() => setIsHowItWorksModalOpen(false)}
        />

        <CalendarModal
          isOpen={isCalendarOpen}
          onClose={() => setIsCalendarOpen(false)}
        />
      </div>
    </>
  );
}